import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import RichTextEditor from '../Common/RichTextEditor';
import TagEditor from '../Common/TagEditor';
import AttachmentManager from '../Common/AttachmentManager';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Create/Edit Faction component with relationship management
 */
const FactionForm = ({ 
  onSuccess, 
  existingEntry = null, 
  suggestedTags = [] 
}) => {
  const [title, setTitle] = useState(existingEntry?.title || '');
  const [description, setDescription] = useState(existingEntry?.description || '');
  const [tags, setTags] = useState(existingEntry?.tags || []);
  const [attachments, setAttachments] = useState(existingEntry?.attachments || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Faction name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (existingEntry) {
        await wikiService.updateWikiEntry(existingEntry.id, {
          title,
          description,
          tags,
          attachments,
        });
        onSuccess();
      } else {
        await wikiService.createWikiEntry({
          type: 'faction',
          title,
          description,
          tags,
          attachments,
          createdBy: 'user',
        });
        onSuccess();
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, backgroundColor: wikiTheme.mediumGray, borderLeft: `4px solid ${wikiTheme.primary}` }}>
      <form onSubmit={handleSubmit}>
        {error && <Alert severity="error" sx={{ mb: 2, fontFamily: wikiTheme.font }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Faction Name */}
          <Box>
            <TextField
              label="Faction Name"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              placeholder="e.g., The Iron Brotherhood, The Emerald Council"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wikiTheme.secondary,
                  fontFamily: wikiTheme.font,
                  '& fieldset': {
                    borderColor: wikiTheme.lightGray,
                  },
                  '&:hover fieldset': {
                    borderColor: wikiTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: wikiTheme.primary,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: wikiTheme.tertiary,
                  fontFamily: wikiTheme.font,
                  '&.Mui-focused': {
                    color: wikiTheme.primary,
                  },
                },
              }}
            />
          </Box>

          {/* Description */}
          <Box>
            <Typography sx={{ mb: 1, color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>
              Description
            </Typography>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Describe this faction, its goals, history, structure, and influence..."
            />
          </Box>

          {/* Tags */}
          <Box>
            <Typography sx={{ mb: 1, color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>
              Tags
            </Typography>
            <TagEditor
              tags={tags}
              onTagsChange={setTags}
              suggestions={suggestedTags}
            />
          </Box>

          {/* Attachments */}
          {existingEntry && (
            <Box>
              <Typography sx={{ mb: 1, color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>
                Attachments
              </Typography>
              <AttachmentManager
                entryId={existingEntry.id}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
              />
            </Box>
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: wikiTheme.primary,
                color: wikiTheme.darkGray,
                fontFamily: wikiTheme.font,
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: wikiTheme.secondary,
                },
                '&:disabled': {
                  backgroundColor: wikiTheme.lightGray,
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {existingEntry ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                existingEntry ? 'Update Faction' : 'Create Faction'
              )}
            </Button>
          </Box>
        </Box>
      </form>
    </Paper>
  );
};

export default FactionForm;
